import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { googleLoginUrl } from '@/utils/authUrl';
import { fetchCurrentUser } from './authSession';
import { API_URL } from '@/utils/apiUrl';
import {
  exchangeInvestorShareToken,
  readInvestorCapability,
  storeInvestorCapability,
  takeInvestorShareToken,
  verifyInvestorCapability,
} from './investorAccess';


const AUTH_KEYS = ['session_token', 'user', 'user_email', 'isLoggedIn', 'auth_timestamp'];

const Storage = {
  get: (key) => {
    try {
      return localStorage.getItem(key) || sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },

  set: (key, value) => {
    let success = false;
    try {
      localStorage.setItem(key, value);
      success = localStorage.getItem(key) === value;
    } catch {
      // sessionStorage may still be available.
    }
    try {
      sessionStorage.setItem(key, value);
      success = sessionStorage.getItem(key) === value || success;
    } catch {
      // localStorage may already contain the value.
    }
    return success;
  },

  remove: (key) => {
    try { localStorage.removeItem(key); } catch {}
    try { sessionStorage.removeItem(key); } catch {}
  },

  clearAuth: () => {
    AUTH_KEYS.forEach((key) => Storage.remove(key));
  }
};

const storeVerifiedUser = (user) => {
  Storage.set('user', JSON.stringify(user));
  Storage.set('isLoggedIn', 'true');
  if (user.email) Storage.set('user_email', user.email);
};

export function useAuth() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      let verifiedUser = null;
      try {
        verifiedUser = await fetchCurrentUser({
          apiUrl: API_URL,
          token: Storage.get('session_token'),
        });
      } catch {
        // Network and malformed-response failures must not authenticate cached state.
      }

      if (cancelled) return;

      if (verifiedUser) {
        storeVerifiedUser(verifiedUser);
        setUser(verifiedUser);
        setIsAuthenticated(true);
      } else {
        Storage.clearAuth();
        setUser(null);
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    initAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const handleStorageChange = async (event) => {
      if (!AUTH_KEYS.includes(event.key)) return;

      if (event.key === 'session_token' && !event.newValue) {
        setUser(null);
        setIsAuthenticated(false);
        return;
      }

      let verifiedUser = null;
      try {
        verifiedUser = await fetchCurrentUser({
          apiUrl: API_URL,
          token: Storage.get('session_token'),
        });
      } catch {
        // Cross-tab state also fails closed when it cannot be verified.
      }

      if (cancelled) return;

      if (verifiedUser) {
        storeVerifiedUser(verifiedUser);
        setUser(verifiedUser);
        setIsAuthenticated(true);
      } else {
        Storage.clearAuth();
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      cancelled = true;
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const login = () => {
    window.location.href = googleLoginUrl();
  };

  const logout = async () => {
    const storedToken = Storage.get('session_token');

    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : {}
      });
    } catch {
      // Local logout must still complete if the API is unavailable.
    }

    Storage.clearAuth();
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = '/';
  };

  return { user, isLoading, isAuthenticated, login, logout };
}

export function ProtectedRoute({ children }) {
  const { isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true, state: { from: location } });
    }
  }, [isLoading, isAuthenticated, navigate, location]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#060710] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/50 text-sm font-mono uppercase tracking-wider">Yükleniyor</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;
  return children;
}

export { Storage };

export function InvestorRoute({ children }) {
  const { isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [accessStatus, setAccessStatus] = useState('checking');

  useEffect(() => {
    let cancelled = false;

    const verifyAccess = async () => {
      if (isLoading) return;
      if (isAuthenticated) {
        setAccessStatus('allowed');
        return;
      }

      const shareToken = takeInvestorShareToken(
        window.location,
        (state, title, url) => window.history.replaceState(state, title, url)
      );

      try {
        if (shareToken) {
          const capability = await exchangeInvestorShareToken({
            apiUrl: API_URL,
            token: shareToken,
          });
          if (!cancelled && capability && storeInvestorCapability(capability)) {
            setAccessStatus('allowed');
            return;
          }
        } else {
          const capability = readInvestorCapability();
          const valid = await verifyInvestorCapability({
            apiUrl: API_URL,
            capability,
          });
          if (!cancelled && valid) {
            setAccessStatus('allowed');
            return;
          }
        }
      } catch {
        // Access verification failures must remain closed.
      }

      if (!cancelled) setAccessStatus('denied');
    };

    verifyAccess();
    return () => {
      cancelled = true;
    };
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    if (accessStatus === 'denied') {
      navigate('/investor?access=required', { replace: true });
    }
  }, [accessStatus, navigate]);

  if (isLoading || accessStatus === 'checking') {
    return (
      <div className="min-h-screen bg-[#060710] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/50 text-sm font-mono uppercase tracking-wider">Yükleniyor</p>
        </div>
      </div>
    );
  }

  if (accessStatus !== 'allowed') return null;
  return children;
}
